/**
 * dropInSpotsForDate — the ONE per-date drop-in spots computation. Extracted
 * verbatim from DropInCalendar's inline getSectionAvailability math so the
 * booking calendar and the public detail page's "Next session · N spots left"
 * line can never disagree. Same relationship, same utility, same data.
 *
 * Input maps mirror the /api/public/programs/availability response shape
 * (memberCounts keyed by sectionId; dropInsByDateAndSection keyed by
 * date → sectionId), so both callers feed it the payload directly.
 */

import { dropInExtraCapacity } from './dropInExtraCapacity';

export type DropInAvailabilityRule = 'always' | 'only_if_not_full' | 'extra_slots';

export interface DropInSpotsForDateArgs {
  dateStr: string; // 'YYYY-MM-DD'
  sectionId: string;
  capacity: number; // the section's base capacity
  availabilityRule: DropInAvailabilityRule;
  extraDropInsAllowed: number; // only applied under 'extra_slots'
  memberCounts: Record<string, number>;
  dropInsByDateAndSection: Record<string, Record<string, number>>;
}

/**
 * spotsLeft = max(totalCapacity − occupied, 0), where
 * totalCapacity = capacity (+ extraDropInsAllowed under 'extra_slots') and
 * occupied = monthly members + booked drop-ins for this date/section.
 */
export function dropInSpotsForDate(args: DropInSpotsForDateArgs): number {
  const memberCount = args.memberCounts[args.sectionId] || 0;
  const dropInCount = (args.dropInsByDateAndSection[args.dateStr] || {})[args.sectionId] || 0;
  const occupied = memberCount + dropInCount;

  const totalCapacity = args.capacity + dropInExtraCapacity({
    drop_in_availability_rule: args.availabilityRule,
    extra_drop_ins_allowed: args.extraDropInsAllowed,
  });

  return Math.max(totalCapacity - occupied, 0);
}
